import { useState } from 'react'
import { GraphqlSubscriptionMessage } from '@aws-amplify/api-graphql'
import { SubscribeSubscription } from '../API'
import { Notification, NotificationEntity, PrometheusAlert } from '../types'
import useSubscribe from '../hooks/useSubscribe'

type NotificationHandler = (
  notification: Notification<NotificationEntity | PrometheusAlert>
) => void

type ErrorHandler = (error: unknown) => void

export default function useSubscribeNotifications(
  handleNotification: NotificationHandler,
  handleError?: ErrorHandler
) {
  /**
   * Resolve the notification.
   */
  const parseNotification = (message: GraphqlSubscriptionMessage<SubscribeSubscription>) => {
    if (message?.data?.subscribe?.data) {
      const event = JSON.parse(message.data.subscribe.data)

      if (typeof event.body === 'string') {
        event.body = JSON.parse(event.body as string)
      }

      return event as Notification<NotificationEntity | PrometheusAlert>
    }
  }

  /**
   * The emitted notification IDs.
   */
  const [emittedNotificationIds, setEmittedNotificationIds] = useState<string[]>([])

  /**
   * Check if the notification has already been emitted.
   */
  const hasNotificationBeenEmitted = (
    notification: Notification<NotificationEntity | PrometheusAlert>
  ) => {
    return emittedNotificationIds.includes(notification.id)
  }

  /**
   * Handle the next message.
   */
  const handleNext = (message: GraphqlSubscriptionMessage<SubscribeSubscription>) => {
    const notification = parseNotification(message)

    // Check if the notification has already been emitted.
    if (notification && !hasNotificationBeenEmitted(notification)) {
      handleNotification(notification)
      setEmittedNotificationIds((prevIds) => [...prevIds, notification.id])
    }
  }

  useSubscribe(handleNext, handleError)
}
